import React, { useEffect, useCallback, useState } from 'react';
import SEO from '../components/SEO';
import { isPrerendering, fallbackProducts, fallbackAccessories } from '../prerenderData';
import HowItWorks from '../components/HowItWorks';
import { useSettings } from '../context/SettingsContext';
import WarrantyDisclaimer from '../components/home/WarrantyDisclaimer';
import HomepageCta from '../components/home/HomepageCta';
import TestimonialSection from '../components/home/TestimonialSection';
import StatsSection from '../components/home/StatsSection';
import FaqSection from '../components/home/FaqSection';
import IntroSection from '../components/home/IntroSection';
import AccessoriesSection from '../components/home/AccessoriesSection';
import SendPhotoSection from '../components/home/SendPhotoSection';
import WhyInvestSection from '../components/home/WhyInvestSection';
import FeaturedProject from '../components/home/FeaturedProject';
import ColourCollection from '../components/home/ColourCollection';
import Hero from '../components/home/Hero';
import MarqueeStrip from '../components/home/MarqueeStrip';
import ProductMap from '../components/home/ProductMap';
import ValueStrip from '../components/home/ValueStrip';
import AwningsShowcase from '../components/home/AwningsShowcase';
import PergolasShowcase from '../components/home/PergolasShowcase';
import NewsletterSection from '../components/home/NewsletterSection';
import QuoteFormSection from '../components/home/QuoteFormSection';
import { HOME_FAQS, AWNING_ORDER } from '../data/homepageData';
import './HomePage.css';
import { apiFetch } from '../services/api';

function HomePage() {
  // CQ-22: settings come from SettingsContext, which already handles the
  // prerender fallback. HomePage still fetches products independently
  // because no other consumer needs them.
  //
  // The first render seeds from the build-time snapshot on the client as
  // well, not only while pre-rendering: it is the same data the pre-rendered
  // HTML was made from, so React can adopt that DOM instead of failing
  // hydration (#418), blanking the lists and rebuilding them once the API
  // answers. Live data replaces the snapshot straight after mount.
  const settings = useSettings();
  const [products, setProducts] = useState([...fallbackProducts, ...fallbackAccessories]);

  useEffect(() => {
    if (isPrerendering) return;
    apiFetch('/api/products')
      .then((r) => r.json())
      .then((data) => setProducts(data))
      .catch(() => {});
  }, []);

  const initObserver = useCallback(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('asg-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.asg-reveal').forEach((el) => observer.observe(el));
    return observer;
  }, []);

  useEffect(() => {
    const observer = initObserver();
    return () => observer.disconnect();
  }, [initObserver]);

  // Re-observe when products load (new .asg-reveal elements appear after async fetch)
  useEffect(() => {
    if (products.length === 0) return;
    const observer = initObserver();
    return () => observer.disconnect();
  }, [products, initObserver]);

  // Split products by category for display. Awning order comes from the
  // shared AWNING_ORDER constant (data/homepageData) so the homepage and
  // the /products/awnings page never drift. Order is pinned by slug —
  // independent of DB sort_order — so it can be tweaked without a database
  // write and stays predictable across the test and prod environments
  // (which share the same MongoDB).
  const PERGOLA_ORDER = [
    'pergola-163',
    'pergola-175',
    'pergola-180-retractable',
  ];
  const orderBy = (slugs) => (a, b) => {
    const ia = slugs.indexOf(a.slug || '');
    const ib = slugs.indexOf(b.slug || '');
    // Unknown slugs sink to the bottom but keep their DB sort_order.
    if (ia === -1 && ib === -1) return (a.sort_order || 99) - (b.sort_order || 99);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  };
  const mainProducts = products.filter((p) => p.category !== 'accessory');
  const awningProducts = mainProducts
    .filter((p) => p.category === 'awning')
    .sort(orderBy(AWNING_ORDER));
  const pergolaProducts = mainProducts
    .filter((p) => p.category !== 'awning')
    .sort(orderBy(PERGOLA_ORDER));
  const accessories = products.filter((p) => p.category === 'accessory');

  // Card image class mapping
  const accImgMap = {};
  accessories.forEach((a, i) => { accImgMap[a._id] = `asg-acc-img-${i + 1}`; });

  return (
    <div className="asg-page">
      <SEO
        title={settings.seo_home_title || undefined}
        description={settings.seo_home_description || 'Made-to-measure retractable awnings and supply-only aluminium pergolas, delivered across Melbourne. Send us photos and measurements and we advise remotely.'}
        keywords={settings.seo_home_keywords || 'retractable awnings Melbourne, motorised awning, cassette awning, pergola supply Melbourne, louvered roof, aluminium pergola'}
        canonical="/"
        ogImage={settings.seo_home_og_image || settings.seo_default_og_image}
        jsonLd={[SEO.localBusinessSchema, SEO.webSiteSchema, SEO.faqSchema(HOME_FAQS), SEO.webPageSchema({ type: 'WebPage', name: 'ASG Shading - Retractable Awnings + Pergola Supply Melbourne', description: 'Premium made-to-measure retractable awnings and supply-only pergola systems, delivered across Melbourne.', url: '/' })]}
      />
      <Hero settings={settings} />

      <MarqueeStrip />

      {settings.home_show_range_map !== false && <ProductMap />}

      <ValueStrip />

      {/* ===== PRODUCTS — straight to the point, in user-requested order
          (awnings → pergolas). Spec comparison TABLES are extracted and
          rendered at the bottom of the page. ===== */}

      <AwningsShowcase awningProducts={awningProducts} />

      {/* Fabric colours follow the awning range: pick a model, then a colour. */}
      <ColourCollection />

      <PergolasShowcase pergolaProducts={pergolaProducts} />

      {/* HOW IT WORKS — moved out of the awnings compare section so it
          frames the whole product block, not just awnings. */}
      <section className="asg-howitworks-wrap asg-reveal">
        <HowItWorks />
      </section>

      {/* Real completed jobs sit directly under the "how it's made" story, so
          the build narrative lands on finished work. */}
      <FeaturedProject settings={settings} />

      <IntroSection settings={settings} />

      <AccessoriesSection accessories={accessories} accImgMap={accImgMap} />

      <SendPhotoSection />

      <WhyInvestSection />


      <StatsSection settings={settings} />

      <TestimonialSection settings={settings} />

      <FaqSection />

      <QuoteFormSection />

      <NewsletterSection />


      <HomepageCta settings={settings} />

      <WarrantyDisclaimer />
    </div>
  );
}

export default HomePage;
